/**
 * GH-268 — "Take the tour" entry for the sidebar help menu.
 *
 * Standalone button consumers can drop into any help menu / settings panel.
 * Calls `useTour().replay()` to (re)start the tour from step 0 regardless
 * of the current state.
 *
 * The sidebar does not yet have a unified Help menu; this component is
 * exported so Phase 4 (or a future sidebar refresh) can mount it where
 * appropriate without re-wiring the tour state machine.
 */

import { LifeBuoy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTour } from '../../features/onboarding-tour/use-tour'

interface HelpMenuTourEntryProps {
  /** Optional className override for menu integration */
  className?: string
}

export function HelpMenuTourEntry({ className }: HelpMenuTourEntryProps) {
  const { t } = useTranslation()
  const { replay } = useTour()

  return (
    <button
      type="button"
      onClick={replay}
      className={
        className ??
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-surface-2'
      }
      data-testid="help-menu-tour-entry"
    >
      <LifeBuoy className="h-3.5 w-3.5" aria-hidden />
      <span>{t('tour.helpMenu.takeTheTour')}</span>
    </button>
  )
}
